import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Avatar,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Business as ClinicIcon,
  LocalHospital as ProviderIcon,
  People as PeopleIcon,
  Assignment as SessionIcon,
  AdminPanelSettings as AdminIcon,
  Badge as StaffIcon,
  Groups as SeenIcon,
  TrendingUp as Trend7Icon,
  DonutLarge as StatusIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import HoverCard3D from '../components/HoverCard3D';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_LABELS = {
  recording: 'Recording',
  processing: 'Processing',
  review: 'Review',
  approved: 'Approved',
  finalized: 'Finalized',
};

function NetworkSvg({ sx }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 200 140"
      sx={{ width: '100%', maxWidth: 220, height: 'auto', opacity: 0.9, ...sx }}
    >
      <defs>
        <linearGradient id="admG1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c9a227" />
          <stop offset="100%" stopColor="#fde68a" />
        </linearGradient>
        <filter id="admShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="6" floodOpacity="0.25" />
        </filter>
      </defs>
      <rect x="8" y="18" width="56" height="44" rx="8" fill="url(#admG1)" filter="url(#admShadow)" opacity="0.95" />
      <rect x="80" y="8" width="52" height="40" rx="8" fill="#1e293b" opacity="0.85" />
      <rect x="136" y="28" width="56" height="48" rx="8" fill="#334155" opacity="0.9" />
      <rect x="72" y="72" width="64" height="52" rx="10" fill="url(#admG1)" filter="url(#admShadow)" opacity="0.75" />
      <path d="M64 40 L80 28 L80 48 Z" fill="#c9a227" opacity="0.5" />
      <path d="M132 48 L136 52 L136 44 Z" fill="#fde68a" opacity="0.6" />
      <circle cx="36" cy="100" r="8" fill="#c9a227" />
      <circle cx="100" cy="108" r="8" fill="#1e293b" />
      <circle cx="164" cy="96" r="8" fill="#c9a227" />
      <path d="M36 100 L100 108 L164 96" stroke="#c9a227" strokeWidth="2" fill="none" opacity="0.4" />
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/superadmin/dashboard/stats`)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // How many sites each organisation owns, derived from the full list so sorting or
  // filtering the table can never change the count a row is judged against.
  const orgSiteCounts = useMemo(() => {
    const counts = {};
    (stats?.clinics || []).forEach((c) => {
      if (c.organization_id) counts[c.organization_id] = (counts[c.organization_id] || 0) + 1;
    });
    return counts;
  }, [stats]);

  const sortedClinics = useMemo(() => {
    const rows = stats?.clinics ? [...stats.clinics] : [];
    rows.sort((a, b) => {
      let av;
      let bv;
      if (orderBy === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
      } else {
        av = Number(a[orderBy]) || 0;
        bv = Number(b[orderBy]) || 0;
      }
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return order === 'asc' ? c : -c;
    });
    return rows;
  }, [stats?.clinics, order, orderBy]);

  const handleSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const fmt = (n) => (n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString());

  const summaryCards = [
    {
      label: 'Clinics & sites',
      value: stats ? `${fmt(stats.active_clinics)} / ${fmt(stats.total_clinics)}` : '—',
      sub: 'active / total',
      icon: <ClinicIcon />,
      color: '#c9a227',
      path: '/clinics',
    },
    {
      label: 'Providers',
      value: fmt(stats?.total_providers),
      sub: 'credentialled',
      icon: <ProviderIcon />,
      color: '#1e40af',
      path: '/providers',
    },
    {
      label: 'Staff',
      value: fmt(stats?.total_staff),
      sub: 'clinic staff users',
      icon: <StaffIcon />,
      color: '#0f766e',
      path: null,
    },
    {
      label: 'Clinic admins',
      value: fmt(stats?.total_clinic_admins),
      sub: 'organization admins',
      icon: <AdminIcon />,
      color: '#7c3aed',
      path: null,
    },
    {
      label: 'Patient roster',
      value: fmt(stats?.total_patients),
      sub: 'records in system',
      icon: <PeopleIcon />,
      color: '#047857',
      path: '/patients',
    },
    {
      label: 'Visits (sessions)',
      value: fmt(stats?.total_sessions),
      sub: 'all time',
      icon: <SessionIcon />,
      color: '#b45309',
      path: null,
    },
    {
      label: 'Patients seen',
      value: fmt(stats?.patients_with_visits),
      sub: '≥1 visit, distinct',
      icon: <SeenIcon />,
      color: '#0369a1',
      path: '/patients',
    },
    {
      label: 'Recent volume',
      value: stats ? `${fmt(stats.sessions_last_7_days)} / ${fmt(stats.sessions_last_30_days)}` : '—',
      sub: 'sessions · 7d / 30d',
      icon: <Trend7Icon />,
      color: '#be123c',
      path: null,
    },
  ];

  const statusEntries = stats?.sessions_by_status
    ? Object.entries(stats.sessions_by_status).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={7}>
          <Box sx={{ pr: { lg: 2 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 700,
                color: '#0f172a',
                mb: 1,
                fontSize: { xs: '1.85rem', md: '2.25rem' },
              }}
            >
              Platform analytics
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0, maxWidth: 560, lineHeight: 1.7 }}>
              Cross-clinic snapshot: rosters, care teams, visit volume, and per-site structure so you can spot growth,
              adoption, and workload at a glance.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} lg={5} sx={{ display: 'flex', justifyContent: { xs: 'center', lg: 'flex-end' } }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 320,
              p: 2,
              borderRadius: 3,
              background: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(241,245,249,0.95) 100%)',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 18px 40px -20px rgba(15, 23, 42, 0.15)',
              transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
              '&:hover': {
                transform: 'perspective(900px) rotateY(-6deg) rotateX(4deg) translateZ(0)',
              },
            }}
          >
            <NetworkSvg />
          </Box>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#c9a227' }} />
        </Box>
      )}

      {!loading && (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {summaryCards.map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.label}>
                <HoverCard3D
                  onClick={() => c.path && navigate(c.path)}
                  cardSx={{
                    cursor: c.path ? 'pointer' : 'default',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${c.color}, ${c.color}88)`,
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', p: 2.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: `${c.color}18`,
                        color: c.color,
                        mr: 2,
                        width: 48,
                        height: 48,
                        boxShadow: `0 8px 20px -6px ${c.color}55`,
                      }}
                    >
                      {c.icon}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h5" fontWeight={800} sx={{ fontFamily: '"DM Sans", sans-serif', lineHeight: 1.2 }}>
                        {c.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {c.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.85 }}>
                        {c.sub}
                      </Typography>
                    </Box>
                  </CardContent>
                </HoverCard3D>
              </Grid>
            ))}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <StatusIcon sx={{ color: '#64748b' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                Sessions by status
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Count of documentation sessions in each workflow stage (all time).
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statusEntries.length === 0 && <Typography variant="body2" color="text.secondary">No sessions yet.</Typography>}
              {statusEntries.map(([key, count]) => (
                <Tooltip key={key} title={key}>
                  <Chip
                    label={`${STATUS_LABELS[key] || key}: ${fmt(count)}`}
                    sx={{
                      fontWeight: 600,
                      borderColor: 'rgba(15, 23, 42, 0.12)',
                      bgcolor: 'rgba(255,255,255,0.9)',
                    }}
                    variant="outlined"
                  />
                </Tooltip>
              ))}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
              mb: 3,
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                  Clinics & hospitals — structure
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Roster size, care team counts, visit volume, and how many distinct patients have been seen (≥1 session).
                </Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/clinics')} sx={{ fontWeight: 700 }}>
                Manage clinics
              </Button>
            </Box>
            <TableContainer sx={{ maxWidth: '100%' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'name' ? order : false}>
                      <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                        Organization
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'provider_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'provider_count'}
                        direction={orderBy === 'provider_count' ? order : 'asc'}
                        onClick={() => handleSort('provider_count')}
                      >
                        Providers
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'staff_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'staff_count'}
                        direction={orderBy === 'staff_count' ? order : 'asc'}
                        onClick={() => handleSort('staff_count')}
                      >
                        Staff
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'clinic_admin_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'clinic_admin_count'}
                        direction={orderBy === 'clinic_admin_count' ? order : 'asc'}
                        onClick={() => handleSort('clinic_admin_count')}
                      >
                        Admins
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'patient_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'patient_count'}
                        direction={orderBy === 'patient_count' ? order : 'asc'}
                        onClick={() => handleSort('patient_count')}
                      >
                        Patients
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'visit_sessions_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'visit_sessions_count'}
                        direction={orderBy === 'visit_sessions_count' ? order : 'asc'}
                        onClick={() => handleSort('visit_sessions_count')}
                      >
                        Visits
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy === 'patients_seen_count' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'patients_seen_count'}
                        direction={orderBy === 'patients_seen_count' ? order : 'asc'}
                        onClick={() => handleSort('patients_seen_count')}
                      >
                        Patients seen
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClinics.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No clinics yet. Create one to see analytics here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedClinics.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                      onClick={() => navigate(`/clinics/${row.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography fontWeight={700}>{row.name}</Typography>
                          {/* Which site of a multi-site customer the others hang off.
                              Hidden on single-site organisations, where every row is
                              trivially the main one and the tag would be noise. */}
                          {row.is_primary_site && orgSiteCounts[row.organization_id] > 1 && (
                            <Chip size="small" color="primary" variant="outlined" label="Main site"
                              sx={{ height: 18, fontSize: 10 }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.address ? `${row.address.slice(0, 56)}${row.address.length > 56 ? '…' : ''}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={(row.type || 'clinic').replace(/_/g, ' ')} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={row.is_active ? 'Yes' : 'No'} color={row.is_active ? 'success' : 'default'} variant={row.is_active ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell align="right">{fmt(row.provider_count)}</TableCell>
                      <TableCell align="right">{fmt(row.staff_count)}</TableCell>
                      <TableCell align="right">{fmt(row.clinic_admin_count)}</TableCell>
                      <TableCell align="right">{fmt(row.patient_count)}</TableCell>
                      <TableCell align="right">{fmt(row.visit_sessions_count)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Distinct patients with at least one session tied to this clinic">
                          <span>{fmt(row.patients_seen_count)}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <HoverCard3D
                cardSx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                      Clinics & hospitals
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/clinics')} sx={{ fontWeight: 700 }}>
                      View all
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Add sites, manage activation, and open a site for full roster and user detail.
                  </Typography>
                </CardContent>
              </HoverCard3D>
            </Grid>
            <Grid item xs={12} md={6}>
              <HoverCard3D
                cardSx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                      Patient registry
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/patients')} sx={{ fontWeight: 700 }}>
                      View all
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Cross-clinic patient list, assignments, and longitudinal visit history.
                  </Typography>
                </CardContent>
              </HoverCard3D>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
